import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, MapPin, Users, Tag, ArrowRight, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { supabase, offerStartsAt, offerCurrentPrice, offerOriginalPrice, offerDiscountPct } from "../lib/supabase";

const formatTime = (d) => d ? d.toLocaleString("es-ES", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" }) : "—";

const ActivityDetail = () => {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("offers")
        .select("*, business:businesses(*)")
        .eq("id", id)
        .maybeSingle();
      setA(data);
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center"><div className="w-10 h-10 border-2 border-sage-deep border-t-transparent rounded-full animate-spin" /></div>;
  if (!a) return <div className="max-w-[900px] mx-auto px-6 py-24 text-center text-stone">Actividad no encontrada. <Link to="/actividades" className="text-sage-deep">Ver más</Link></div>;

  const business = a.business || {};
  const starts = offerStartsAt(a);
  const currentPrice = offerCurrentPrice(a);
  const originalPrice = offerOriginalPrice(a);
  const discountPct = offerDiscountPct(a);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-8 md:py-12" data-testid="offer-detail-page">
      <Link to="/actividades" className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-stone hover:text-sage-deep transition mb-8">
        <ArrowLeft size={14} /> Volver
      </Link>
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
          <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-forest/10">
            {(a.image_url || business.image_url) && <img src={a.image_url || business.image_url} alt={a.title} className="w-full h-full object-cover" />}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {discountPct > 0 && <span className="bg-sage-deep text-paper px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full">-{discountPct}% OFF</span>}
              {business.category && <span className="bg-paper/90 backdrop-blur-sm text-forest px-3 py-1.5 text-xs uppercase tracking-wider rounded-full">{business.category}</span>}
            </div>
          </div>

          <div className="mt-8 space-y-6">
            <h1 className="font-heading text-4xl md:text-5xl font-light leading-tight text-forest">{a.title}</h1>
            <p className="text-stone text-lg leading-relaxed">{a.description}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4">
              <Info icon={<Clock size={18} />} l="Hora" v={formatTime(starts)} />
              <Info icon={<MapPin size={18} />} l="Centro" v={business.name || "—"} />
              <Info icon={<Users size={18} />} l="Plazas" v={`${a.available_slots} libres`} />
              <Info icon={<Tag size={18} />} l="Categoría" v={business.category || "—"} />
            </div>

            {business.description && (
              <div className="pt-6 border-t border-forest/10 space-y-2">
                <h3 className="font-heading text-xl font-semibold text-forest">Sobre {business.name}</h3>
                <p className="text-stone text-sm">{business.description}</p>
                {business.address && <p className="text-stone text-sm flex items-center gap-1"><MapPin size={12} /> {business.address}</p>}
              </div>
            )}

            <div className="pt-6 border-t border-forest/10 space-y-3">
              <h3 className="font-heading text-xl font-semibold text-forest">Qué incluye</h3>
              <ul className="space-y-2 text-stone">
                {["Acceso completo a la sesión", "Plaza confirmada al instante", "Código QR de acceso enviado por email"].map((t) => (
                  <li key={t} className="flex gap-2 items-start"><CheckCircle size={18} weight="fill" className="text-sage-deep mt-0.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        <motion.aside initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="lg:sticky lg:top-24 self-start">
          <div className="border border-forest/10 bg-white rounded-2xl p-8 space-y-6 shadow-[0_8px_32px_-12px_rgba(44,62,45,0.10)]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone">Precio actual</div>
              <div className="flex items-baseline gap-3 mt-2">
                <span className="font-heading text-5xl font-medium text-sage-deep">{currentPrice.toFixed(2)}€</span>
                {discountPct > 0 && <span className="text-ash line-through">{originalPrice.toFixed(2)}€</span>}
              </div>
              {discountPct > 0 && <div className="text-xs text-sage-deep mt-1">Ahorras {(originalPrice - currentPrice).toFixed(2)}€</div>}
            </div>

            <div className="space-y-2 text-sm text-stone">
              <div className="flex justify-between"><span>Inicio</span><span className="text-forest">{formatTime(starts)}</span></div>
              <div className="flex justify-between"><span>Plazas</span><span className="text-forest">{a.available_slots} disponibles</span></div>
            </div>

            <button onClick={() => navigate(`/checkout/${a.id}`)} disabled={a.available_slots <= 0} data-testid="reserve-now-btn"
              className="w-full bg-sage-deep text-paper py-4 font-medium tracking-wide text-sm hover:bg-forest transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 rounded-full">
              {a.available_slots > 0 ? <>Reservar a {currentPrice.toFixed(2)}€ <ArrowRight size={16} /></> : "Sin plazas"}
            </button>
            <p className="text-[11px] text-ash text-center">No requiere registro · Pago seguro</p>
          </div>
        </motion.aside>
      </div>
    </div>
  );
};

const Info = ({ icon, l, v }) => (
  <div className="border border-forest/10 bg-white rounded-xl p-4">
    <div className="flex items-center gap-2 text-sage-deep">{icon}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-stone mt-2">{l}</div>
    <div className="text-sm font-medium mt-1 line-clamp-2 text-forest">{v}</div>
  </div>
);

export default ActivityDetail;
